import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Typography, 
  Box, 
  CircularProgress, 
  Alert, 
  Button, 
  Card, 
  Chip,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tooltip
} from "@mui/material";
import { API_ENDPOINTS } from "../config/api.config";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import HelpIcon from '@mui/icons-material/Help';
import ProjectUsers from '../components/ProjectUsers';
import ProjectTeams from '../components/ProjectTeams';

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  
  // States per il dialog di modifica
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    customer_id: '',
    start_date: '',
    end_date: '',
    active: true,
    project_code: '',
    days_after_block: 1,
    days_before_block: 1,
    ticket_id_prefix: '',
    regex: '',
    regex_desc: '',
    previous_month_blocked: false,
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [regexHelpOpen, setRegexHelpOpen] = useState(false);

  // Activities tab state
  const [activities, setActivities] = useState([]);
  const [projectActivityIds, setProjectActivityIds] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesSaving, setActivitiesSaving] = useState(false);
  const [activitiesError, setActivitiesError] = useState(null);

  // useEffect posizionato dopo le funzioni con useCallback per evitare warning "used before defined"

  const fetchProject = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[PROJECT] Fetching project:', id);
      const response = await fetch(`${API_ENDPOINTS.PROJECTS}?id=${id}`);
      console.log('[PROJECT] Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('[PROJECT] Error response:', errorText);
        throw new Error(`API Error ${response.status}: ${errorText || 'Unknown error'}`);
      }
      
      const data = await response.json();
      console.log('[PROJECT] Response data:', data);
      const projectData = data.project || data;
      console.log('[PROJECT] Project loaded:', projectData);
      setProject(projectData);
    } catch (err) {
      console.error('[PROJECT] Fetch error:', err);
      setError(`Error loading project: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const handleBack = () => {
    navigate('/projects');
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatSimpleDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const fetchCustomers = useCallback(async () => {
    setCustomersLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.CUSTOMERS);
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || data || []);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  const handleEditDialog = () => {
    if (!project) return;
    
    // Debug: log delle date ricevute dal backend
    console.log('[PROJECT EDIT] Date from backend:', {
      start_date: project.start_date,
      end_date: project.end_date
    });
    
    const getFormattedDate = (dateStr) => {
      if (!dateStr) return '';
      try {
        // Se la data è già in formato YYYY-MM-DD, restituiscila direttamente
        if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateStr;
        }
        // Se la data contiene 'T' (formato ISO con orario), estrai solo la parte data
        if (typeof dateStr === 'string' && dateStr.includes('T')) {
          return dateStr.split('T')[0];
        }
        // Fallback: converti tramite Date object
        const date = new Date(dateStr);
        // Usa le componenti locali per evitare shift di timezone
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch (error) {
        return '';
      }
    };

    const formattedStartDate = getFormattedDate(project.start_date);
    const formattedEndDate = getFormattedDate(project.end_date);
    
    // Debug: log delle date formattate per il form
    console.log('[PROJECT EDIT] Formatted dates for form:', {
      start_date: formattedStartDate,
      end_date: formattedEndDate
    });

    setFormData({
      name: project.name || '',
      description: project.description || '',
      customer_id: project.customer_id || '',
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      active: project.active !== false,
      project_code: project.project_code || '',
      days_after_block: project.days_after_block || 1,
      days_before_block: project.days_before_block || 1,
      ticket_id_prefix: project.ticket_id_prefix || '',
      regex: project.regex || '',
      regex_desc: project.regex_desc || '',
      previous_month_blocked: project.previous_month_blocked || false,
    });
    setFormError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormError(null);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleRegexHelpOpen = () => {
    setRegexHelpOpen(true);
  };

  const handleRegexHelpClose = () => {
    setRegexHelpOpen(false);
  };

  // ===== Activities Tab functions =====
  const fetchAllActivities = useCallback(async () => {
    try {
      const resp = await fetch(API_ENDPOINTS.ACTIVITIES);
      if (!resp.ok) return;
      const data = await resp.json();
      setActivities(data.activities || []);
    } catch (e) {
      // silent
    }
  }, []);

  const fetchProjectActivities = useCallback(async () => {
    if (!id) return;
    setActivitiesLoading(true);
    setActivitiesError(null);
    try {
      const resp = await fetch(`${API_ENDPOINTS.PROJECT_ACTIVITIES}?project_id=${id}`);
      if (!resp.ok) {
        const err = await resp.text();
        throw new Error(err || 'Failed to load project activities');
      }
      const data = await resp.json();
      setProjectActivityIds(data.activity_ids || []);
    } catch (e) {
      setActivitiesError(e.message);
    } finally {
      setActivitiesLoading(false);
    }
  }, [id]);

  const toggleActivitySelection = (activityId) => {
    setProjectActivityIds((prev) => {
      const set = new Set(prev);
      if (set.has(activityId)) {
        set.delete(activityId);
      } else {
        set.add(activityId);
      }
      return Array.from(set);
    });
  };

  const handleSaveActivities = async () => {
    if (!id) return;
    setActivitiesSaving(true);
    setActivitiesError(null);
    try {
      const resp = await fetch(`${API_ENDPOINTS.PROJECT_ACTIVITIES}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: parseInt(id, 10), activity_ids: projectActivityIds })
      });
      if (!resp.ok) {
        const err = await resp.text();
        throw new Error(err || 'Errore nel salvataggio delle activities');
      }
      await fetchProjectActivities();
    } catch (e) {
      setActivitiesError(e.message);
    } finally {
      setActivitiesSaving(false);
    }
  };

  const handleSaveProject = async () => {
    setSaving(true);
    setFormError(null);
    
    if (!formData.name.trim()) {
      setFormError('Project name is required.');
      setSaving(false);
      return;
    }

    if (!formData.project_code.trim()) {
      setFormError('Project code is required.');
      setSaving(false);
      return;
    }

    if (!formData.customer_id) {
      setFormError('Customer is required.');
      setSaving(false);
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        customer_id: parseInt(formData.customer_id),
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        active: formData.active,
        project_code: formData.project_code.trim(),
        days_after_block: parseInt(formData.days_after_block) || 1,
        days_before_block: parseInt(formData.days_before_block) || 1,
        ticket_id_prefix: formData.ticket_id_prefix.trim() || null,
        regex: formData.regex.trim() || null,
        regex_desc: formData.regex_desc.trim() || null,
        previous_month_blocked: formData.previous_month_blocked,
      };

      console.log('[PROJECT SAVE] Payload dates being sent to backend:', {
        start_date: payload.start_date,
        end_date: payload.end_date
      });
      console.log('[PROJECT] Update payload:', payload);

      const response = await fetch(`${API_ENDPOINTS.PROJECTS}?id=${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error ${response.status}: ${errorText || 'Unknown error'}`);
      }

      const result = await response.json();
      console.log('[PROJECT] Update result:', result);

      // Refresh project data
      await fetchProject();
      
      // Close dialog
      setOpenDialog(false);
      
    } catch (err) {
      console.error('[PROJECT] Update error:', err);
      setFormError(`Error updating project: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ===== useEffect hooks =====
  useEffect(() => {
    if (id) {
      fetchProject();
      fetchCustomers();
      fetchAllActivities();
      fetchProjectActivities();
    }
  }, [id, fetchProject, fetchCustomers, fetchAllActivities, fetchProjectActivities]);

  function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`project-tabpanel-${index}`}
        aria-labelledby={`project-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 4 }}>
            {children}
          </Box>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box my={4}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBack}
          variant="outlined"
        >
          Back to Projects
        </Button>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box my={4}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Project not found
        </Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBack}
          variant="outlined"
        >
          Back to Projects
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box my={4}>
        {/* Header con titolo, back button e edit button */}
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontSize: '1.5rem', flexGrow: 1 }}>
            Project Details for
            <Box
              component="span"
              sx={{
                display: { xs: 'block', md: 'inline' },
                bgcolor: '#e0e0e0',
                color: '#607c89',
                fontWeight: 500,
                fontSize: '0.9em',
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                ml: { xs: 0, md: 1 },
                mt: { xs: 1, md: 0 }
              }}
            >
              {project.name}
            </Box>
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<EditIcon />}
            onClick={handleEditDialog} 
            sx={{ 
              ml: 2,
              backgroundColor: '#388e3c',
              '&:hover': {
                backgroundColor: '#2e7d32'
              }
            }}
          >
            Edit Project Details
          </Button>
        </Box>

        {/* Tabs Navigation */}
        <Card sx={{ mb: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange} 
              aria-label="project information tabs"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: '#388e3c',
                },
                '& .MuiTab-root.Mui-selected': {
                  color: '#388e3c',
                },
              }}
            >
              <Tab 
                icon={<InfoIcon />}
                label="General" 
                id="project-tab-0"
                aria-controls="project-tabpanel-0"
                iconPosition="start"
                sx={{
                  '& .MuiTab-iconWrapper + span': {
                    display: { xs: 'none', sm: 'inline' },
                    marginLeft: { sm: '8px' }
                  }
                }}
              />

              <Tab 
                icon={<PeopleIcon />}
                label="Users" 
                id="project-tab-1"
                aria-controls="project-tabpanel-1"
                iconPosition="start"
                sx={{
                  '& .MuiTab-iconWrapper + span': {
                    display: { xs: 'none', sm: 'inline' },
                    marginLeft: { sm: '8px' }
                  }
                }}
              />
              <Tab 
                icon={<GroupsIcon />}
                label="Teams" 
                id="project-tab-2"
                aria-controls="project-tabpanel-2"
                iconPosition="start"
                sx={{
                  '& .MuiTab-iconWrapper + span': {
                    display: { xs: 'none', sm: 'inline' },
                    marginLeft: { sm: '8px' }
                  }
                }}
              />
              <Tab 
                icon={<SettingsIcon />}
                label="Activities" 
                id="project-tab-3"
                aria-controls="project-tabpanel-3"
                iconPosition="start"
                sx={{
                  '& .MuiTab-iconWrapper + span': {
                    display: { xs: 'none', sm: 'inline' },
                    marginLeft: { sm: '8px' }
                  }
                }}
              />
            </Tabs>
          </Box>

          {/* Tab Panel 1: General Information */}
          <TabPanel value={currentTab} index={0}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
              {/* Prima colonna: Informazioni base */}
              <Box>
                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    Project Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1.2rem', color: '#388e3c' }}>
                    {project.name}
                  </Typography>
                </Box>

                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    Project Code
                  </Typography>
                  <Chip 
                    label={project.project_code || 'N/A'} 
                    variant="outlined"
                    sx={{ 
                      fontWeight: 500, 
                      fontFamily: 'monospace',
                      borderColor: '#388e3c',
                      color: '#fff',
                      backgroundColor: '#388e3c'
                    }}
                  />
                </Box>

        <Box mb={3}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
            Ticket ID Prefix
          </Typography>
          <Chip 
            label={project.ticket_id_prefix || 'Not set'} 
            variant="outlined"
            sx={{ 
              fontWeight: 500, 
              fontFamily: 'monospace',
              borderColor: project.ticket_id_prefix ? '#1976d2' : '#9e9e9e',
              color: project.ticket_id_prefix ? '#fff' : '#666',
              backgroundColor: project.ticket_id_prefix ? '#1976d2' : '#f5f5f5'
            }}
          />
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
            Ticket ID Regex
          </Typography>
          <Chip 
            label={project.regex || 'Not set'} 
            variant="outlined"
            sx={{ 
              fontWeight: 500, 
              fontFamily: 'monospace',
              borderColor: project.regex ? '#1976d2' : '#9e9e9e',
              color: project.regex ? '#fff' : '#666',
              backgroundColor: project.regex ? '#1976d2' : '#f5f5f5'
            }}
          />
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
            Regex Description
          </Typography>
          {project.regex_desc ? (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {project.regex_desc}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No description provided
            </Typography>
          )}
        </Box>

                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    Description
                  </Typography>
                  <Typography variant="body1" sx={{ fontStyle: project.description ? 'normal' : 'italic', color: project.description ? 'text.primary' : 'text.secondary' }}>
                    {project.description || 'No description provided'}
                  </Typography>
                </Box>

                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    Customer
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#388e3c' }}>
                    {project.customer?.name || 'N/A'}
                  </Typography>
                </Box>

                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    Status
                  </Typography>
                  <Chip 
                    label={project.active ? 'Active' : 'Inactive'} 
                    color={project.active ? 'success' : 'default'}
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                </Box>
              </Box>

              {/* Seconda colonna: Timeline e Date */}
              <Box>
                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    Start Date
                  </Typography>
                  <Typography variant="body1">
                    {formatSimpleDate(project.start_date)}
                  </Typography>
                </Box>

                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    End Date
                  </Typography>
                  <Typography variant="body1">
                    {formatSimpleDate(project.end_date)}
                  </Typography>
                </Box>

                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    Project Duration
                  </Typography>
                  <Typography variant="body1">
                    {project.start_date && project.end_date ? (
                      (() => {
                        const start = new Date(project.start_date);
                        const end = new Date(project.end_date);
                        const diffTime = Math.abs(end - start);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        return `${diffDays} days`;
                      })()
                    ) : 'N/A'}
                  </Typography>
                </Box>

                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    Worklog Restrictions
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Chip 
                      label={`${project.days_before_block || 1} days before`} 
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem', alignSelf: 'flex-start' }}
                    />
                    <Chip 
                      label={`${project.days_after_block || 1} days after`} 
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem', alignSelf: 'flex-start' }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Checkbox
                        checked={project.previous_month_blocked || false}
                        disabled
                        size="small"
                        sx={{ p: 0.5 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Previous month blocked
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Terza colonna: System Info */}
              <Box>
                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    Project ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '1.1rem' }}>
                    {project.id}
                  </Typography>
                </Box>

                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    Created At
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(project.created_at)}
                  </Typography>
                </Box>

                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    Last Updated
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(project.updated_at)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </TabPanel>



          {/* Tab Panel 2: Users Management */}
          <TabPanel value={currentTab} index={1}>
            <ProjectUsers projectId={id} projectName={project.name} />
          </TabPanel>

          {/* Tab Panel 3: Teams Management */}
          <TabPanel value={currentTab} index={2}>
            <ProjectTeams projectId={id} projectName={project.name} />
          </TabPanel>

          {/* Tab Panel 4: Activities */}
          <TabPanel value={currentTab} index={3}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Activities</Typography>
              {activitiesError && (
                <Alert severity="error" sx={{ mb: 2 }}>{activitiesError}</Alert>
              )}
              {activitiesLoading ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Caricamento activities...</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                  {activities.map((act) => (
                    <FormControlLabel
                      key={act.id}
                      control={
                        <Checkbox
                          checked={projectActivityIds.includes(act.id)}
                          onChange={() => toggleActivitySelection(act.id)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{act.name}</Typography>
                          {act.description && (
                            <Typography variant="caption" color="text.secondary">{act.description}</Typography>
                          )}
                        </Box>
                      }
                    />
                  ))}
                </Box>
              )}
              <Box sx={{ mt: 2 }}>
                <Button
                  onClick={handleSaveActivities}
                  variant="contained"
                  startIcon={activitiesSaving ? <CircularProgress size={16} /> : <ScheduleIcon />}
                  disabled={activitiesSaving}
                  sx={{ backgroundColor: '#388e3c', '&:hover': { backgroundColor: '#2e7d32' } }}
                >
                  {activitiesSaving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Box>
          </TabPanel>
        </Card>
      </Box>

      {/* Dialog per modifica progetto */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth 
        scroll="paper" 
        sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
        PaperProps={{ sx: { mt: 6, mb: 6 } }}
      >
        <DialogTitle>
          Edit Project
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Prima riga: Nome e Codice */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                name="name"
                label="Project Name"
                value={formData.name}
                onChange={handleFormChange}
                required
                fullWidth
                error={!formData.name.trim() && formError}
                helperText={!formData.name.trim() && formError ? 'Name is required' : ''}
              />
              <TextField
                name="project_code"
                label="Project Code"
                value={formData.project_code}
                onChange={handleFormChange}
                required
                fullWidth
                error={!formData.project_code.trim() && formError}
                helperText={!formData.project_code.trim() && formError ? 'Project code is required' : ''}
              />
            </Box>
            
            {/* Seconda riga: Customer */}
            <FormControl fullWidth required error={!formData.customer_id && formError}>
              <InputLabel>Customer</InputLabel>
              <Select
                name="customer_id"
                value={formData.customer_id}
                onChange={handleFormChange}
                label="Customer"
                disabled={customersLoading}
              >
                {customersLoading ? (
                  <MenuItem value="">
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading customers...
                  </MenuItem>
                ) : (
                  customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            
            {/* Terza riga: Ticket ID Prefix */}
            <TextField
              name="ticket_id_prefix"
              label="Ticket ID Prefix"
              value={formData.ticket_id_prefix}
              onChange={handleFormChange}
              fullWidth
              placeholder="e.g., SAAAMSABH-, SAAMSELT-, JIRA-"
              helperText="Optional prefix for ticket IDs. When set, this prefix will be automatically added to ticket IDs for ticket resolution activities."
            />
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <TextField
                name="regex"
                label="Regex Pattern"
                value={formData.regex}
                onChange={handleFormChange}
                fullWidth
                placeholder="e.g., /^[A-Z]{2}[0-9]{4}$/i"
                helperText="Optional regex pattern for ticket ID validation. If ticket ID Prefix is valued, this regex will be used to validate the text after the prefix."
              />
              <Tooltip title="Click for regex help">
                <IconButton 
                  onClick={handleRegexHelpOpen}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  <HelpIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              name="regex_desc"
              label="Regex Description"
              value={formData.regex_desc}
              onChange={handleFormChange}
              fullWidth
              multiline
              rows={2}
              placeholder="e.g., Description shown to users when entering Ticket ID"
              helperText="This description will be shown to users when inserting/updating worklogs to explain the expected Ticket ID format."
            />
            
            {/* Quarta riga: Date */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                name="start_date"
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={handleFormChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                name="end_date"
                label="End Date"
                type="date"
                value={formData.end_date}
                onChange={handleFormChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            
            {/* Quinta riga: Block Days */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                📅 <strong>Worklog Time Restrictions:</strong> These fields control how many days before and after the current date users are allowed to insert or update worklogs for this project.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  name="days_before_block"
                  label="Days Before Block"
                  type="number"
                  value={formData.days_before_block}
                  onChange={handleFormChange}
                  fullWidth
                  inputProps={{ min: 0, max: 365 }}
                  helperText="Number of days before current date to block worklog entries"
                />
                <TextField
                  name="days_after_block"
                  label="Days After Block"
                  type="number"
                  value={formData.days_after_block}
                  onChange={handleFormChange}
                  fullWidth
                  inputProps={{ min: 0, max: 365 }}
                  helperText="Number of days after current date to block worklog entries"
                />
              </Box>
              
              {/* Checkbox per previous_month_blocked */}
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="previous_month_blocked"
                      checked={formData.previous_month_blocked}
                      onChange={handleFormChange}
                      color="primary"
                    />
                  }
                  label="Block previous month worklog entries"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 4 }}>
                  When enabled, users cannot create or modify worklogs for the previous month
                </Typography>
              </Box>
            </Box>

            {/* Sesta riga: Description */}
            <TextField
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleFormChange}
              multiline
              rows={3}
              fullWidth
            />

            {/* Settima riga: Active Status */}
            <FormControlLabel
              control={
                <Checkbox
                  name="active"
                  checked={formData.active}
                  onChange={handleFormChange}
                  color="primary"
                />
              }
              label="Project is active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSaveProject}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
        </Dialog>

        {/* Regex Help Dialog */}
        <Dialog 
          open={regexHelpOpen} 
          onClose={handleRegexHelpClose}
          maxWidth="md"
          fullWidth
          scroll="paper"
          sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
          PaperProps={{ sx: { mt: 6, mb: 6 } }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HelpIcon color="primary" />
              <Typography variant="h6">Regex Pattern Help</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              How to fill in the Ticket ID regex field
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              A <strong>regex</strong> helps check that your Ticket IDs follow the correct pattern. If you don’t know how to write one, just use one of the examples below or change the letters/numbers to fit your needs.
            </Typography>

            <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Ready-to-use examples:
            </Typography>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mb: 2 }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ textAlign: 'left', borderBottom: '1px solid #e0e0e0', py: 1 }}>Ticket Format</Box>
                  <Box component="th" sx={{ textAlign: 'left', borderBottom: '1px solid #e0e0e0', py: 1 }}>Use this regex</Box>
                </Box>
              </Box>
              <Box component="tbody">
                <Box component="tr">
                  <Box component="td" sx={{ py: 1 }}>ABC-123 (Jira)</Box>
                  <Box component="td" sx={{ py: 1 }}><code>^[A-Z]+-\d+$</code></Box>
                </Box>
                <Box component="tr">
                  <Box component="td" sx={{ py: 1 }}>12345 (Numbers only)</Box>
                  <Box component="td" sx={{ py: 1 }}><code>^\d+$</code></Box>
                </Box>
                <Box component="tr">
                  <Box component="td" sx={{ py: 1 }}>INC0012345 (ServiceNow)</Box>
                  <Box component="td" sx={{ py: 1 }}><code>^INC\d+$</code></Box>
                </Box>
                <Box component="tr">
                  <Box component="td" sx={{ py: 1 }}>REQ45A (Letters+Numbers)</Box>
                  <Box component="td" sx={{ py: 1 }}><code>^[A-Za-z0-9]+$</code></Box>
                </Box>
              </Box>
            </Box>

            <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              How to set exact or maximum length limits
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <li>
                <Typography variant="body2"><strong>To require exactly 5 numbers</strong>: Use <code>^\d{'{'}5{'}'}$</code></Typography>
                <Typography variant="body2">Example valid: <code>12345</code>. Not valid: <code>1234</code>, <code>123456</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>To require between 3 and 6 letters</strong>: Use <code>^[A-Za-z]{'{'}3,6{'}'}$</code></Typography>
                <Typography variant="body2">Example valid: <code>ABC</code>, <code>abcd</code>, <code>AbCdEf</code>. Not valid: <code>AB</code>, <code>ABCDEFG</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>To require at most 4 digits</strong>: Use <code>^\d{'{'}1,4{'}'}$</code></Typography>
                <Typography variant="body2">Example valid: <code>1</code>, <code>1234</code>. Not valid: <code>12345</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>To require at least 3 digits</strong>: Use <code>^\d{'{'}3,{'}'}$</code></Typography>
                <Typography variant="body2">Example valid: <code>123</code>, <code>1234567</code></Typography>
              </li>
            </Box>

            <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              What do the main symbols mean?
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <li><Typography variant="body2"><code>^</code> → means “must start with”</Typography></li>
              <li><Typography variant="body2"><code>$</code> → means “must end with”</Typography></li>
              <li><Typography variant="body2"><code>[A-Z]</code> → one UPPERCASE letter</Typography></li>
              <li><Typography variant="body2"><code>[a-z]</code> → one lowercase letter</Typography></li>
              <li><Typography variant="body2"><code>\d</code> → a digit (0 to 9)</Typography></li>
              <li><Typography variant="body2"><code>+</code> → one or more of the previous character</Typography></li>
              <li><Typography variant="body2"><code>{'{'}n{'}'}</code> → exactly n characters</Typography></li>
              <li><Typography variant="body2"><code>{'{'}n,m{'}'}</code> → between n and m characters (inclusive)</Typography></li>
              <li><Typography variant="body2"><code>{'{'}n,{'}'}</code> → at least n characters</Typography></li>
            </Box>

            <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Practical examples
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 1 }}>
              <li>
                <Typography variant="body2"><strong>Exactly 5 numbers ("12345")</strong>: Use <code>^\d{5}$</code></Typography>
                <Typography variant="body2">Valid: <code>12345</code>. Not valid: <code>1234</code>, <code>123456</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>Numbers only, any length ("12345")</strong>: Use <code>^\d+$</code></Typography>
                <Typography variant="body2">Valid: <code>1987</code>, <code>00234</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>Jira ticket ("ABC-123")</strong>: Use <code>^[A-Z]+-\d+$</code></Typography>
                <Typography variant="body2">Valid: <code>DEF-45</code>. Not valid: <code>def-45</code>, <code>DEF_45</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>ServiceNow ticket ("INC0012345")</strong>: Use <code>^INC\d+$</code></Typography>
                <Typography variant="body2">Valid: <code>INC123</code>. Not valid: <code>INC-123</code>, <code>INCabc</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>Letters and numbers mixed ("REQ45A")</strong>: Use <code>^[A-Za-z0-9]+$</code></Typography>
                <Typography variant="body2">Valid: <code>AB123C</code>. Not valid: <code>REQ 123</code>, <code>REQ-123</code></Typography>
              </li>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Tip:</strong> Test your regex patterns using online tools like regex101.com or regexr.com before implementing them in your project.
                If you need more help, feel free to open a ticket.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRegexHelpClose} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }


